import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as CryptoJS from "crypto-js";
import { Observable } from "rxjs";

@Injectable()
export class EncryptInterceptor implements HttpInterceptor {
  private SECRET_KEY = CryptoJS.enc.Utf8.parse("0123456789ABCDEF0123456789ABCDEF"); // 32-byte key
  private IV = CryptoJS.enc.Utf8.parse("ABCDEF1234567890"); // 16-byte IV

  ignoreUrl: RegExp[] = [
    /^\/analytics\/download(?:AdvVectoringMission|BasicVectoringLesson)AnalyticsUsage(?:Graph)?$/,
    /^\/analytics\/downloadBasicVectoringLessonAndLevelAnalyticsUsage$/,
    /^\/folderAnalytics\/[a-f0-9]+\/recordUsage$/,
    /^\/atc\/analytics\/folderAnalytics\/[a-f0-9]+\/recordUsage$/,
    /^\/generalAnalytics\/recordUsage$/,
    /^\/practiceSetAnalytics\/[a-f0-9]+\/recordUsage$/,
    /^\/atc\/analytics\/practiceSetAnalytics\/[a-f0-9]+\/recordUsage$/,
    /^\/getImageFileHash$/,
    /^\/saveFlexTableDataFile$/,
    /^\/validatingCSVMemorization$/,
    /^\/validatingCSVWordJumbleAndMatch$/,
    /^\/downloadFile$/,
    /^\/importCSVFileInSets$/,
    /^\/saveLogo$/,
    /^\/llama3\/getMCQFromUploadDocument$/,
    /^\/llama3\/getFillBlanksFromUploadDocument$/,
    /^\/training-material(\/.*)?$/, 
    /^\/feedback\/api\/applications\/[a-f0-9-]+\/users\/[a-f0-9]+\/logs\/recent$/i,
    /^\/feedback\/api\/applications\/[a-f0-9-]+\/logs\/\d+\/feedback$/i,
    /^\/feedback\/api\/logs$/i,
    /^\/api\/emails\/send$/i,
  ];

  private getEmailServicePathPrefix(): string {
    try {
      const raw = localStorage.getItem("ui-config");
      if (!raw) return "";
      const cfg = JSON.parse(raw);
      const baseUrl: string = cfg?.EMAIL_SERVICE_BASE_URL || "";
      if (!baseUrl) return "";
      let pathname = baseUrl.startsWith("http") ? new URL(baseUrl).pathname : baseUrl;
      // Normalize: ensure leading slash, strip trailing slash
      if (!pathname.startsWith("/")) pathname = "/" + pathname;
      return pathname.replace(/\/+$/, "");
    } catch {
      return "";
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const origin = window.location.origin;
    if (origin.includes("localhost")) {
      return next.handle(req);
    }
    const xEncrypt = true;

    // Extract the base URL path without query parameters
    let compareUrl = req?.url;
    if (compareUrl?.includes("/v1")) {
      compareUrl = compareUrl.split("/v1")[1];
    }
    compareUrl = compareUrl?.split("?")[0];
    // If still an absolute URL, extract just the pathname
    if (compareUrl?.startsWith("http")) {
      try {
        compareUrl = new URL(compareUrl).pathname;
      } catch (_) {}
    }

    // Strip EMAIL_SERVICE_BASE_URL pathname prefix (e.g. "/emailapp")
    // so the configured deployment prefix doesn't need to be hardcoded in ignoreUrl
    const emailPrefix = this.getEmailServicePathPrefix();
    if (emailPrefix && compareUrl?.startsWith(emailPrefix + "/")) {
      compareUrl = compareUrl.substring(emailPrefix.length);
    }

    // Check if URL should be ignored
    if (this.ignoreUrl.some((pattern) => pattern.test(compareUrl))) {
      return next.handle(req);
    }

    // FormData (file uploads) can't be JSON.stringify'd meaningfully — it serializes to "{}",
    // and replacing the body would also drop the multipart/form-data Content-Type + boundary,
    // causing a 415 Unsupported Media Type on the backend. Pass these through unmodified.
    if (req.body instanceof FormData) {
      return next.handle(req);
    }

    // Handle request with or without body
    if (req.body) {
      const encryptedBody = xEncrypt ? this.encrypt(JSON.stringify(req.body)) : req.body;
      const encryptedReq = req.clone({
        body: xEncrypt ? { payload: encryptedBody } : req.body,
        headers: req.headers.set("X-Encrypted", xEncrypt.toString()),
      });
      return next.handle(encryptedReq);
    } else {
      const encryptedReq = req.clone({
        headers: req.headers.set("X-Encrypted", xEncrypt.toString()),
      });
      return next.handle(encryptedReq);
    }
  }

  private encrypt(text: any): string {
    const encrypted = CryptoJS.AES.encrypt(text, this.SECRET_KEY, {
      iv: this.IV,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  }
}
